import { Component } from '@theme/component';

const BLOOMREACH_CARD_VIEW = 'bloomreach-card';
const CARD_VISIBILITY_MARGIN = '300px';
/** Track product-card-template.eta and .br-products__grid (4 up from 750px, 2
 * below); 1280w keeps a 4-up card sharp at 2x on a widescreen desktop. */
const IMAGE_WIDTHS = [160, 320, 480, 640, 800, 960, 1280, 1600];
const IMAGE_SIZES = '(min-width: 750px) 25vw, 50vw';
/**
 * Desktop card UI is gated by breakpoint and primary pointer, never touch
 * capability (SM-526: `any-pointer: coarse` gave touch laptops the mobile
 * gallery). Same gate as product-card-size-strip.js and plp-collections-custom.css.
 */
const DESKTOP_HOVER_QUERY = '(min-width: 750px) and (hover: hover) and (pointer: fine)';
const GALLERY_IMAGE_COUNT = 2;
const isDesktopHover = () => {
  try {
    return window.matchMedia(DESKTOP_HOVER_QUERY).matches;
  } catch {
    return false;
  }
};

/**
 * Builds a Shopify CDN srcset, matching the ladder the card template uses for
 * the primary image so the two swap at the same resolution.
 *
 * @param {string} imageUrl - A Shopify CDN image URL.
 * @returns {string} A srcset descriptor list.
 */
function buildSrcset(imageUrl) {
  const joiner = imageUrl.includes('?') ? '&' : '?';
  return IMAGE_WIDTHS.map((width) => `${imageUrl}${joiner}width=${width} ${width}w`).join(',');
}

/** Matches the card template: two swatches, then a +N count. */
const VISIBLE_COLOURWAYS = 2;

/**
 * Builds one colourway swatch, matching the card template's markup so a
 * single stylesheet covers both the feed-rendered and JS-rendered cases.
 *
 * Display only (SM-523): a span, not a link — no href, no tabbable element,
 * no pointer cursor. The colour is still announced through role="img".
 *
 * @param {{label: string, url: string, swatch: string, kind: string}} colourway
 * @returns {HTMLSpanElement}
 */
function colourwaySwatch(colourway) {
  const wrapper = document.createElement('span');
  wrapper.className = 'ceres-card__style-colourway';
  wrapper.setAttribute('role', 'img');
  wrapper.setAttribute('aria-label', colourway.label || 'Other colourway');

  const swatch = document.createElement('span');
  swatch.className = 'swatch';
  swatch.style.setProperty('--swatch-background', colourway.swatch);
  swatch.setAttribute('aria-hidden', 'true');

  wrapper.append(swatch);
  return wrapper;
}

/** @type {Map<string, Promise<string | null>>} */
const fragmentCache = new Map();

/**
 * Hydrates server-rendered Shopify card features into a Bloomreach card.
 *
 * @typedef {object} BloomreachProductCardRefs
 * @property {HTMLElement} badges - Shared Liquid badge output.
 * @property {HTMLElement} quickShop - Horizon quick-add context.
 * @property {HTMLElement} [swatches] - Swatch cluster, target for colourways.
 * @property {HTMLElement} [image] - Image wrapper, target for the hover image.
 * @property {HTMLElement} [sizes] - Inline size row, unless the feed filled it.
 * @property {HTMLElement} [promo] - Promo slot, unless the feed filled it.
 * @property {HTMLElement} [notify] - Notify-me panel (assets/back-in-stock.js).
 * @property {HTMLElement} [liveRegion] - Screen-reader status region for the quick-buy
 *   add outcome. Optional: docs/loomi/product-card-template.eta deploys via a manual
 *   console paste, separately from this JS, so an older live template may not carry it yet.
 * @extends {Component<BloomreachProductCardRefs>}
 */
class BloomreachProductCard extends Component {
  requiredRefs = ['badges', 'quickShop'];

  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  /** Guards the quick-buy bar's fetch against a second tap before it resolves. */
  #quickBuySubmitting = false;

  /**
   * The quick-buy module once a shopper has triggered its import, so
   * disconnect can clear its timers without loading it for a card that never
   * added anything.
   *
   * @type {Promise<typeof import('@theme/bloomreach-card-quick-buy')> | null}
   */
  #quickBuyModule = null;

  /**
   * This card's own notify-me panel, cloned out of its fragment and kept
   * detached until a sold-out size is clicked.
   * @type {HTMLElement | null}
   */
  #notifyMarkup = null;
  /** @type {MutationObserver | null} */
  #clusterObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#neutraliseLegacyCluster();
    this.refs.badges.setAttribute('aria-busy', 'true');
    this.addEventListener('submit', this.#handleQuickBuySubmit);
    this.addEventListener('click', this.#handleAddButtonClick);
    this.addEventListener('change', this.#handleSizeChange);
    this.addEventListener('pointerdown', this.#recordChipState);
    this.addEventListener('click', this.#handleRepeatChipClick);
    this.addEventListener('mouseleave', this.#resetQuickBuySuccess);

    if (!('IntersectionObserver' in window)) {
      void this.#hydrate();
      return;
    }

    this.#visibilityObserver = new IntersectionObserver(this.#handleVisibility, {
      rootMargin: CARD_VISIBILITY_MARGIN,
    });
    this.#visibilityObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#visibilityObserver?.disconnect();
    this.#clusterObserver?.disconnect();
    this.#clusterObserver = null;
    this.removeEventListener('submit', this.#handleQuickBuySubmit);
    this.removeEventListener('click', this.#handleAddButtonClick);
    this.removeEventListener('change', this.#handleSizeChange);
    this.removeEventListener('pointerdown', this.#recordChipState);
    this.removeEventListener('click', this.#handleRepeatChipClick);
    this.removeEventListener('mouseleave', this.#resetQuickBuySuccess);
    this.#quickBuyModule?.then(({ clearQuickBuyTimers }) => clearQuickBuyTimers(this)).catch(() => {});
  }

  /**
   * Desktop quick buy: choosing a size is the add — the "+" does not have to
   * be pressed after it. The change fires once per newly selected chip and
   * hands off to the form's submit path (#handleQuickBuySubmit), which does
   * the fetch and swaps the "+" for the success check. Coarse pointers never
   * see the bar: the stylesheet hides the form and the theme quick add's size
   * sheet is the add path there, so the pointer gate keeps this desktop-only.
   *
   * @param {Event} event
   */
  #handleSizeChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('ceres-card__size-input')) return;
    this.#submitSizeChip(input);
  };

  /**
   * A click on the chip that is already selected fires no change event, so
   * adding the same size twice would otherwise do nothing. Radios flip
   * `checked` before click handlers run, so the pre-click state is captured
   * on pointerdown and read back in #handleRepeatChipClick.
   *
   * @param {PointerEvent} event
   */
  #recordChipState = (event) => {
    if (!(event.target instanceof Element)) return;
    const input = event.target.closest('.ceres-card__size')?.querySelector('.ceres-card__size-input');
    if (input instanceof HTMLInputElement) input.dataset.wasChecked = String(input.checked);
  };

  /** @param {MouseEvent} event */
  #handleRepeatChipClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const input = event.target.closest('.ceres-card__size')?.querySelector('.ceres-card__size-input');
    if (!(input instanceof HTMLInputElement)) return;

    // The label's click re-dispatches on the input, so this runs twice per
    // tap; the flag is consumed on the first pass.
    const wasChecked = input.dataset.wasChecked === 'true';
    delete input.dataset.wasChecked;
    if (!wasChecked || !input.checked) return;

    this.#submitSizeChip(input);
  };

  /**
   * Submits the desktop quick-buy bar for a chosen chip. Unavailable chips
   * open the notify panel instead (#handleSizeClick) and never reach here.
   *
   * @param {HTMLInputElement} input - The chosen size chip's radio.
   */
  #submitSizeChip(input) {
    if (input.disabled || input.closest('.ceres-card__size--unavailable')) return;
    if (!isDesktopHover()) return;

    const form = input.form;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('ceres-card__form')) return;

    form.requestSubmit();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleVisibility = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;

    this.#visibilityObserver?.disconnect();
    void this.#hydrate();
  };

  async #hydrate() {
    const fragmentUrl = this.#fragmentUrl;
    if (!fragmentUrl) {
      this.refs.badges.removeAttribute('aria-busy');
      return;
    }

    try {
      let fragmentRequest = fragmentCache.get(fragmentUrl);
      if (!fragmentRequest) {
        fragmentRequest = fetch(fragmentUrl, { headers: { Accept: 'text/html' } })
          .then((response) => (response.ok ? response.text() : null))
          .catch(() => null);
        fragmentCache.set(fragmentUrl, fragmentRequest);
      }

      const responseText = await fragmentRequest;
      if (!responseText) {
        // A transient fetch failure must not poison the cache for the page's
        // lifetime — evict so the next hydration attempt refetches.
        fragmentCache.delete(fragmentUrl);
        return;
      }
      if (!this.isConnected) return;

      const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
      const badgeMarkup = responseDocument.querySelector('.product-card-badges');
      if (badgeMarkup) this.refs.badges.replaceChildren(badgeMarkup.cloneNode(true));

      try {
        this.#renderSizes(responseDocument);
        this.#renderHoverImage(responseDocument);
        this.#setupMobileGallery();
        this.#renderColourways(responseDocument);
        this.#renderPromo(responseDocument);
      } finally {
        if (responseDocument.querySelector('[data-promo-card]')) {
          void import('@theme/promo-price-overlay').then((m) => m.applyPromoOverlay(this, responseDocument)).catch(() => {});
        }
      }
      this.#renderNotify(responseDocument);

      // At the desktop breakpoint the theme's quick add is the fallback for.
      const hasOwnSizeRow = Boolean(this.refs.sizes?.querySelector('input'));
      const desktopHoverUi = isDesktopHover();
      const injectQuickAdd = !hasOwnSizeRow || !desktopHoverUi;

      const quickAddMarkup = injectQuickAdd ? responseDocument.querySelector('quick-add-component') : null;
      const quickAddTarget = this.refs.quickShop.querySelector('[data-bloomreach-quick-shop-target]');
      if (quickAddMarkup && quickAddTarget) {
        const quickAdd = /** @type {HTMLElement} */ (quickAddMarkup.cloneNode(true));
        // A product with exactly one variant has nothing to choose: the.
        const singleVariant = quickAddMarkup.getAttribute('data-quick-add-button') === 'add';
        quickAdd.dataset.quickAddButton = singleVariant ? 'add' : 'choose';
        if (hasOwnSizeRow && !singleVariant) {
          // Sized products reuse the complete-the-look context so the shared.
          quickAdd.dataset.quickAddContext = 'complete-the-look';
        }
        quickAddTarget.replaceChildren(quickAdd);

        // A single sold-out variant's "Add" button renders natively disabled
        // (there's no size to choose), so it can never dispatch a click for
        // #handleAddButtonClick to catch. Un-disable it: that handler treats
        // it exactly like the size row's own unavailable chip.
        const addButton = quickAdd.querySelector('.quick-add__button--add');
        if (singleVariant && addButton instanceof HTMLButtonElement) addButton.disabled = false;
      }
    } finally {
      if (this.isConnected) this.refs.badges.removeAttribute('aria-busy');
    }
  }

  /**
   * Fills the card's inline size row from the fragment's variant JSON.
   *
   * Only runs when the card template rendered an empty row — a row already
   * filled from Discovery feed data is the newer path and wins, so this can be
   * deleted wholesale once variant_title is populated in the index.
   *
   * @param {Document} responseDocument - The parsed card fragment.
   */
  #renderSizes(responseDocument) {
    const { sizes } = this.refs;
    if (!sizes || sizes.querySelector('input')) return;

    const payload = responseDocument.querySelector('[data-bloomreach-variants]')?.textContent;
    if (!payload) return;

    /**
     * @type {Array<{id: number, label: string, available: boolean,
     *   back_in_stock: boolean, inventory_quantity: number | null,
     *   sku: string | null, price: number | null,
     *   price_formatted: string | null}>}
     */
    let variants;
    try {
      variants = JSON.parse(payload);
    } catch {
      return;
    }
    if (!Array.isArray(variants)) return;

    // A variant.id of 0 is deliberate (templates/product.bloomreach-card.liquid):
    // a size with no backing Shopify variant still renders, muted — `id`
    // must stay out of this filter or it disappears entirely. It's never
    // notify-eligible though (SM-771): id 0 means not sold at all here, vs
    // a real sold-out variant, so `back_in_stock` gates the panel below.
    const displayableSizes = variants.filter((variant) => variant.label);
    if (!displayableSizes.length) return;

    // A one-size product ("OS") has nothing to choose. Leaving its only chip.
    const singleSize = displayableSizes.length === 1 && displayableSizes[0]?.available === true;

    const row = document.createDocumentFragment();
    for (const variant of displayableSizes) {
      const label = document.createElement('label');
      label.className = 'ceres-card__size';

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'id';
      input.value = String(variant.id);
      input.className = 'ceres-card__size-input';
      if (!variant.available) {
        label.classList.add('ceres-card__size--unavailable');
        if (variant.back_in_stock) {
          input.dataset.notifyEligible = 'true';
          input.setAttribute('aria-label', `${variant.label} — Out of stock — Notify me`);
        } else {
          // Not sold in this colour (SM-771) — muted, no notify panel.
          input.setAttribute('aria-label', `${variant.label} — Unavailable`);
        }
      }
      // Unavailable sizes stay visible, muted (.ceres-card__size--unavailable).
      if (variant.sku) input.dataset.variantSku = variant.sku;
      if (typeof variant.price === 'number') input.dataset.variantPrice = String(variant.price);
      if (variant.price_formatted) input.dataset.variantPriceFormatted = variant.price_formatted;
      // No size pre-selects — the design shows none chosen. `required` lets.
      input.required = true;
      if (singleSize) input.checked = true;

      const text = document.createElement('span');
      text.className = 'ceres-card__size-label';
      text.textContent = variant.label;

      label.append(input, text);
      row.append(label);
    }

    sizes.append(row);
    sizes.addEventListener('click', this.#handleSizeClick);
  }

  /**
   * Intercepts a tap on an unavailable size chip: the desktop quick-buy bar
   * keeps these chips enabled (Figma "quick buy", muted colour) rather than
   * disabling them, so a click must be caught before the browser checks the
   * radio and opened as the notify-me panel — only when notify-eligible
   * (SM-771); otherwise the click is just swallowed, like the PDP's inert
   * "Unavailable" chip.
   *
   * @param {MouseEvent} event
   */
  #handleSizeClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const label = event.target.closest('.ceres-card__size--unavailable');
    if (!label) return;

    const input = label.querySelector('.ceres-card__size-input');
    if (!(input instanceof HTMLInputElement)) return;

    event.preventDefault();
    if (input.dataset.notifyEligible !== 'true') return;
    this.#openNotify(input);
  };

  /**
   * Opens the notify-me panel for a tapped unavailable size. The drawer, the
   * <back-in-stock> definition and the field patching live in
   * assets/bloomreach-card-notify.js and are imported on interaction.
   *
   * Callers must already have called preventDefault() synchronously: the await
   * below yields, so anything left to the browser's default behaviour would
   * have happened before the module resolves.
   *
   * @param {HTMLInputElement} input - The tapped size's radio input.
   */
  async #openNotify(input) {
    if (!this.#notifyMarkup) return;

    const { openNotify } = await import('@theme/bloomreach-card-notify');
    openNotify({ markup: this.#notifyMarkup, cardKey: this.dataset.productUrl || '', input });
  }

  /**
   * Keeps this card's notify-me panel markup ready to mount. The shared host
   * it mounts into is created on first open — why it is shared, and why it
   * cannot live in the shadow root, is documented on mountNotifyPanel in
   * assets/bloomreach-card-notify.js.
   *
   * @param {Document} responseDocument - The parsed card fragment.
   */
  #renderNotify(responseDocument) {
    const markup = responseDocument.querySelector('[data-bloomreach-notify] back-in-stock');
    if (markup) this.#notifyMarkup = /** @type {HTMLElement} */ (markup.cloneNode(true));
  }

  /**
   * Catches a click on a one-size sold-out product's "+" button — the
   * desktop quick-buy bar's `.ceres-card__add`, or the theme's injected
   * mobile quick-add's `.quick-add__button--add` (#hydrate un-disables it for
   * exactly this case) — before either button's own default behaviour runs.
   *
   * The size chip's own `required` radio is never pre-checked when it's
   * unavailable (#renderSizes), so a native click on the desktop bar would
   * fail constraint validation and silently swallow the submit — no `submit`
   * event fires for `#handleQuickBuySubmit` to catch, and the injected button
   * has no chip to check at all. Both route to the notify panel instead, as
   * the size row does (#handleSizeClick). `stopImmediatePropagation` stops the
   * injected button's own `on:click="/handleClick"` (assets/product-form.js)
   * animating a fly-to-cart for an add that never happens.
   *
   * @param {MouseEvent} event
   */
  #handleAddButtonClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest('.ceres-card__add, .quick-add__button--add')) return;

    const sizeInputs = this.refs.sizes?.querySelectorAll('.ceres-card__size-input') ?? [];
    const onlySize = sizeInputs.length === 1 ? sizeInputs[0] : null;
    if (!(onlySize instanceof HTMLInputElement) || onlySize.checked) return;
    if (!onlySize.closest('.ceres-card__size--unavailable')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (onlySize.dataset.notifyEligible !== 'true') return; // SM-771
    this.#openNotify(onlySize);
  };

  /**
   * Intercepts the desktop quick-buy submit and hands it to
   * assets/bloomreach-card-quick-buy.js, imported on this first add rather
   * than on every PLP: SM-659 and the card size-chip work together put this
   * file over the per-file AssetSizeJavaScript budget, which is pinned to the
   * vendor Horizon baseline and is not ours to widen. preventDefault() and
   * the in-flight guard run synchronously, before the await, so nothing falls
   * through to the browser's native POST.
   *
   * @param {SubmitEvent} event
   */
  #handleQuickBuySubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    // Two forms can add from a card. The desktop quick-buy bar is the card's
    // own `.ceres-card__form`. On a coarse pointer the bridge injects the
    // theme's quick add instead (#hydrate) and that is the only add path.
    // The theme binds `on:submit` by delegation on `document`
    // (assets/component.js), but `submit` is composed: false, so it never
    // leaves the widget's shadow root and the theme's handler never runs:
    // the browser did a native POST and dropped the shopper on /cart with
    // the item already added (SM-537). Both forms settle through the fetch in
    // the imported module so the drawer opens and the shopper stays put.
    const isCardForm = form.classList.contains('ceres-card__form');
    const isInjectedQuickAdd = Boolean(form.closest('[data-bloomreach-quick-shop-target]'));
    if (!isCardForm && !isInjectedQuickAdd) return;

    // The card form takes its variant from the checked size chip; the
    // injected quick add carries one hidden variant input instead.
    const variantInput = isCardForm
      ? form.querySelector('input[name="id"]:checked')
      : form.querySelector('input[name="id"]');
    // No size picked: let the native `required` validation stop the submit
    // and surface its own UI rather than fetching with no variant. (A
    // one-size sold-out product never gets here at all — #handleAddButtonClick
    // catches that case on the button's own click, before the browser's
    // validation swallows the submit.)
    if (!(variantInput instanceof HTMLInputElement) || !variantInput.value || this.#quickBuySubmitting) return;

    event.preventDefault();
    this.#quickBuySubmitting = true;

    this.#quickBuyModule ??= import('@theme/bloomreach-card-quick-buy');
    this.#quickBuyModule
      .then(({ submitQuickBuy }) => submitQuickBuy(this, form, variantInput))
      .catch((error) => console.warn('[bloomreach-product-card] Quick buy failed to load:', error))
      .finally(() => {
        this.#quickBuySubmitting = false;
      });
  };

  /**
   * Winds back the quick-buy success state on mouseleave. The state only ever
   * exists after an add, which has already loaded the module, so the guard
   * keeps a plain hover off the import.
   */
  #resetQuickBuySuccess = () => {
    const addButton = this.querySelector('.ceres-card__add');
    if (!(addButton instanceof HTMLElement) || addButton.dataset.added !== 'true') return;

    this.#quickBuyModule ??= import('@theme/bloomreach-card-quick-buy');
    this.#quickBuyModule.then(({ resetQuickBuySuccess }) => resetQuickBuySuccess(this)).catch(() => {});
  };


  /**
   * Fills an empty promo slot from the fragment's promo JSON (the
   * custom.promo_message metaobject). A slot the feed already filled wins,
   * so this can go once the index carries promo_message. Colours travel as
   * custom properties and only literal hex is accepted, as on the feed path.
   *
   * @param {Document} responseDocument - The parsed card fragment.
   */
  #renderPromo(responseDocument) {
    const { promo } = this.refs;
    if (!promo || promo.textContent.trim()) return;

    const payload = responseDocument.querySelector('[data-bloomreach-promo]')?.textContent;
    if (!payload) return;

    /** @type {{text: string, background_colour: string, text_colour: string}} */
    let message;
    try {
      message = JSON.parse(payload);
    } catch {
      return;
    }
    if (typeof message?.text !== 'string' || !message.text) return;

    const isSafeHex = (value) =>
      typeof value === 'string' && value.startsWith('#') && (value.length === 4 || value.length === 7);

    promo.textContent = message.text;
    if (isSafeHex(message.background_colour)) {
      promo.style.setProperty('--promo-background', message.background_colour);
    }
    if (isSafeHex(message.text_colour)) {
      promo.style.setProperty('--promo-text', message.text_colour);
    }
  }

  /**
   * Adds the hover image the card template could not render, for the same
   * reason as the sizes: the Discovery feed carries no second image. Delete
   * alongside #renderSizes once alt_image reaches the index.
   *
   * @param {Document} responseDocument - The parsed card fragment.
   */
  #renderHoverImage(responseDocument) {
    const { image } = this.refs;
    if (!image || image.querySelector('.ceres-card__image-hover')) return;

    const payload = responseDocument.querySelector('[data-bloomreach-media]')?.textContent;
    if (!payload) return;

    /** @type {{src: string, alt: string}} */
    let media;
    try {
      media = JSON.parse(payload);
    } catch {
      return;
    }
    if (!media?.src) return;

    const hover = document.createElement('img');
    hover.className = 'ceres-card__image-hover';
    hover.src = media.src;
    hover.srcset = buildSrcset(media.src);
    hover.sizes = IMAGE_SIZES;
    hover.alt = '';
    hover.loading = 'lazy';
    hover.setAttribute('aria-hidden', 'true');

    image.append(hover);
  }

  /**
   * Wires the mobile swipe gallery per the Figma M-PLP card: outside the
   * desktop hover UI the touch CSS turns the primary and second image into
   * scroll-snap slides, and pagination dots at the image's bottom-left track
   * the active slide. The dots come from the card template when the feed
   * carried the second image; this builds them when the second image arrived
   * via the fragment instead, then binds the one scroll listener either way.
   * Desktop hover behaviour is untouched — a desktop-width touch laptop keeps
   * the hover swap rather than the swipe gallery (SM-526).
   */
  #setupMobileGallery() {
    const { image } = this.refs;
    if (!image || isDesktopHover()) return;
    if (!image.querySelector('.ceres-card__image-hover')) return;

    const media = image.closest('.ceres-card__media');
    if (!media) return;

    let dots = media.querySelector('.ceres-card__dots');
    if (!dots) {
      dots = document.createElement('div');
      dots.className = 'ceres-card__dots';
      // Purely decorative — the slides are one product's two angles, and the
      // media link already labels the destination.
      dots.setAttribute('aria-hidden', 'true');
      for (let index = 0; index < GALLERY_IMAGE_COUNT; index += 1) {
        const dot = document.createElement('span');
        dot.className = 'ceres-card__dot';
        if (index === 0) dot.classList.add('ceres-card__dot--active');
        dots.append(dot);
      }
      media.append(dots);
    }

    const markers = dots.querySelectorAll('.ceres-card__dot');
    image.addEventListener(
      'scroll',
      () => {
        if (!image.clientWidth) return;
        // Math.abs keeps the index positive under RTL scroll coordinates.
        const active = Math.round(Math.abs(image.scrollLeft) / image.clientWidth);
        markers.forEach((marker, index) => {
          marker.classList.toggle('ceres-card__dot--active', index === active);
        });
      },
      { passive: true },
    );
  }

  /**
   * Rewrites an older card template's cluster (links, +N disclosure, button
   * swatches) into inert spans (SM-522, SM-523). No-op on current markup.
   */
  #neutraliseLegacyCluster() {
    const cluster = this.querySelector('.ceres-card__swatches');
    if (!cluster) return;

    // An older app-side paste renders both clusters; style colourways win.
    if (cluster.querySelector('.ceres-card__style-colourways')) {
      for (const variants of cluster.querySelectorAll('.ceres-card__variants')) variants.remove();
    }
    this.#dedupeColourways(cluster);
    // The widget re-renders the card after this bridge has filled it, which
    // can leave two colourway groups side by side; collapse them as they land.
    if (!this.#clusterObserver) {
      this.#clusterObserver = new MutationObserver(() => this.#dedupeColourways(cluster));
      this.#clusterObserver.observe(cluster, { childList: true });
    }

    const inertCount = (text, label) => {
      const count = document.createElement('span');
      count.className = 'ceres-card__style-colourways-overflow-count hidden-swatches__count';
      count.setAttribute('role', 'img');
      count.setAttribute('aria-label', label);
      const digits = document.createElement('span');
      digits.setAttribute('aria-hidden', 'true');
      digits.textContent = text;
      count.append(digits);
      return count;
    };

    for (const disclosure of cluster.querySelectorAll('details')) {
      const text = disclosure.querySelector('summary')?.textContent.trim() || '';
      const hidden = disclosure.querySelectorAll('.swatch').length || Number(text.replace(/\D/g, '')) || 0;
      disclosure.replaceWith(inertCount(text || `+${hidden}`, `${hidden} more colourways`));
    }

    for (const control of cluster.querySelectorAll('a, button')) {
      const label = (control.getAttribute('aria-label') || control.getAttribute('title') || control.textContent)
        .trim()
        .replace(/^(View|Show) /, '');
      const swatch = control.querySelector('.swatch');
      if (!swatch) {
        control.replaceWith(inertCount(control.textContent.trim(), label));
        continue;
      }
      const inert = document.createElement('span');
      inert.className = control.classList.contains('ceres-card__variant-swatch')
        ? control.className
        : 'ceres-card__style-colourway';
      inert.setAttribute('role', 'img');
      inert.setAttribute('aria-label', label || 'Colourway');
      inert.append(swatch);
      control.replaceWith(inert);
    }
  }

  /**
   * Fills the card's swatch cluster with the style's other colourways,
   * mirroring the card template's markup so one stylesheet covers both.
   *
   * @param {Document} responseDocument - The parsed card fragment.
   */
  /**
   * Keeps a single colourway group: the widget's own wins, and only the
   * group this bridge appended is ever removed, so the widget's re-render
   * never sees its markup vanish and re-insert it in a loop.
   */
  #dedupeColourways(cluster) {
    const groups = [...cluster.querySelectorAll('.ceres-card__style-colourways')];
    if (groups.length < 2) return;
    const widgetOwned = groups.some((group) => !group.hasAttribute('data-bridge-colourways'));
    for (const group of groups.slice(1)) {
      if (!widgetOwned || group.hasAttribute('data-bridge-colourways')) group.remove();
    }
    if (widgetOwned && groups[0].hasAttribute('data-bridge-colourways')) groups[0].remove();
  }

  #renderColourways(responseDocument) {
    const { swatches } = this.refs;
    // The app-side card template may lag this bridge by a paste; its older
    // link-flavoured class still means the feed already rendered the cluster.
    if (!swatches || swatches.querySelector('.ceres-card__style-colourway, .ceres-card__style-colourway-link')) {
      return;
    }

    const payload = responseDocument.querySelector('[data-bloomreach-colourways]')?.textContent;
    if (!payload) return;

    /** @type {Array<{label: string, url: string, swatch: string, kind: string}>} */
    let colourways;
    try {
      colourways = JSON.parse(payload);
    } catch {
      return;
    }
    if (!Array.isArray(colourways)) return;

    const usable = colourways.filter((colourway) => colourway.url && colourway.swatch);
    // Swatches are flat colour only, never a product image. An image-backed.
    const flat = usable.filter((colourway) => colourway.kind !== 'image');
    if (!flat.length) return;

    const group = document.createElement('div');
    group.className = 'ceres-card__style-colourways';
    group.setAttribute('data-bridge-colourways', '');

    const visible = flat.slice(0, VISIBLE_COLOURWAYS);
    for (const colourway of visible) {
      group.append(colourwaySwatch(colourway));
    }

    const hidden = usable.slice(visible.length);
    if (hidden.length) {
      // A count, not a disclosure (SM-522): no summary to click or focus, and.
      const overflow = document.createElement('span');
      overflow.className = 'ceres-card__style-colourways-overflow-count hidden-swatches__count';
      overflow.setAttribute('role', 'img');
      overflow.setAttribute('aria-label', `${hidden.length} more colourways`);

      const count = document.createElement('span');
      count.setAttribute('aria-hidden', 'true');
      count.textContent = `+${hidden.length}`;
      overflow.append(count);

      group.append(overflow);
    }

    swatches.append(group);
  }

  get #fragmentUrl() {
    const productUrl = this.dataset.productUrl;
    if (!productUrl) return null;

    try {
      const url = new URL(productUrl, window.location.origin);
      if (url.origin !== window.location.origin) return null;

      url.search = '';
      url.searchParams.set('view', BLOOMREACH_CARD_VIEW);
      return url.toString();
    } catch {
      return null;
    }
  }
}

if (!customElements.get('bloomreach-product-card')) {
  customElements.define('bloomreach-product-card', BloomreachProductCard);
}
